import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from 'firebase/auth';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';

const EMULATOR_HOST = '127.0.0.1';
export const EMULATOR_PORTS = {
  auth: 9099,
  functions: 5001,
  firestore: 8080,
  storage: 9199,
  ui: 4000,
};

/** Opt in to live Firebase from a local `npm start`. Never the default. */
export const USE_PRODUCTION = process.env.REACT_APP_USE_PRODUCTION === 'true';

/**
 * Local development talks to the Firebase Emulator Suite, not production.
 * Production builds (`npm run build`) and `npm run start:prod` use live Firebase.
 */
export const USE_FIREBASE_EMULATORS =
  !USE_PRODUCTION &&
  (process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true' ||
    process.env.NODE_ENV === 'development');

const emulatorDefaults = {
  apiKey: 'demo-api-key',
  authDomain: 'localhost',
  projectId: 'diologic-44440',
  storageBucket: 'diologic-44440.appspot.com',
  messagingSenderId: '123456789',
  appId: '1:123456789:web:local-emulator',
};

const envOr = (value, fallback) =>
  value && value !== 'YOUR_API_KEY' && value !== 'your-project-id' && value !== 'YOUR_APP_ID'
    ? value
    : fallback;

const firebaseConfig = USE_FIREBASE_EMULATORS
  ? {
      apiKey: envOr(process.env.REACT_APP_FIREBASE_API_KEY, emulatorDefaults.apiKey),
      authDomain: envOr(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN, emulatorDefaults.authDomain),
      projectId: envOr(process.env.REACT_APP_FIREBASE_PROJECT_ID, emulatorDefaults.projectId),
      storageBucket: envOr(process.env.REACT_APP_FIREBASE_STORAGE_BUCKET, emulatorDefaults.storageBucket),
      messagingSenderId: envOr(
        process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        emulatorDefaults.messagingSenderId
      ),
      appId: envOr(process.env.REACT_APP_FIREBASE_APP_ID, emulatorDefaults.appId),
    }
  : {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    };

const isConfigured =
  firebaseConfig.apiKey &&
  firebaseConfig.apiKey !== 'YOUR_API_KEY' &&
  firebaseConfig.projectId &&
  firebaseConfig.projectId !== 'your-project-id';

let app;
let auth;
let googleProvider;
let storage;
let db;
let functions;

function connectEmulatorsOnce() {
  if (typeof window === 'undefined') return;
  if (window.__DIOLOGIC_FIREBASE_EMULATORS_CONNECTED__) return;
  window.__DIOLOGIC_FIREBASE_EMULATORS_CONNECTED__ = true;

  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, EMULATOR_HOST, EMULATOR_PORTS.firestore);
  connectStorageEmulator(storage, EMULATOR_HOST, EMULATOR_PORTS.storage);
  connectFunctionsEmulator(functions, EMULATOR_HOST, EMULATOR_PORTS.functions);

  console.info(
    `[Firebase] Using local emulators (UI http://${EMULATOR_HOST}:${EMULATOR_PORTS.ui}). Production data is not used.`
  );
}

if (isConfigured) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    googleProvider = new GoogleAuthProvider();
    storage = getStorage(app);
    functions = getFunctions(app, 'us-central1');
    const dbId = process.env.REACT_APP_FIRESTORE_DATABASE_ID?.trim();
    try {
      db = dbId ? getFirestore(app, dbId) : getFirestore(app);
    } catch (dbError) {
      console.warn('Named Firestore database failed, falling back to default:', dbError?.message);
      db = getFirestore(app);
    }
    if (USE_FIREBASE_EMULATORS) {
      connectEmulatorsOnce();
    } else if (typeof window !== 'undefined') {
      getAnalytics(app);
      // Do NOT enable IndexedDB persistence for this app.
      // Offline cache + full-array writes to userManagement/* caused consultants
      // to disappear minutes later when a stale tab synced (last-write-wins).
    }
  } catch (error) {
    console.error('Firebase initialization failed:', error.message);
    throw error;
  }
} else {
  console.warn(
    USE_PRODUCTION
      ? 'Firebase production credentials are missing. Copy .env.example to .env.local and add live project values.'
      : 'Firebase is not configured. Copy .env.example to .env.development or run npm run local.'
  );
  app = { name: '[DEFAULT]', options: {} };
  auth = {
    currentUser: null,
    onAuthStateChanged: (callback) => {
      setTimeout(() => callback(null), 100);
      return () => {};
    },
    signInWithPopup: () =>
      Promise.reject(new Error('Firebase not configured. See docs/LOCAL_ENVIRONMENT.md')),
    signOut: () => Promise.reject(new Error('Firebase not configured')),
  };
  googleProvider = {};
  storage = { ref: () => ({ put: () => Promise.reject(new Error('Firebase not configured')) }) };
  db = null;
  functions = null;
}

export function getFunctionsBaseUrl() {
  const projectId = app?.options?.projectId;
  const region = 'us-central1';
  if (!projectId) return null;
  if (USE_FIREBASE_EMULATORS) {
    return `http://${EMULATOR_HOST}:${EMULATOR_PORTS.functions}/${projectId}/${region}`;
  }
  return `https://${region}-${projectId}.cloudfunctions.net`;
}

export { auth, googleProvider, storage, db, functions };
export default app;
